/***
 *
 *   CONTACT / USER EDIT
 *   Phase 7 — reference ContactDetailPanel chrome on the existing deep-linkable
 *   route (/app/contacts/:userId and /app/settings/users/:userId). Keep the
 *   route; do not move to a slide-over.
 *
 **********/

import React, { useEffect, useContext } from 'react';
import {
    Card,
    SettingsWrapper,
    usePermissions,
    TicketItem,
    ViewContext,
    Button,
    UserAvatar,
    Badge,
    Skeleton,
    EmptyState,
    Icon,
    useProjectContext,
} from 'components/lib';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import { useApiContext } from 'app/api';
import { UserForm } from 'components/form/template/user';
import { useTranslate } from 'app/translations';
import Style from './edit.tailwind';
import { useBreadcrumbsContext } from 'context/BreadcrumbsContext';
import { ContactProjectsPannel } from 'components/contacts/contactProjectsPannel';

const EditUser = () => {
    const { userId } = useParams();
    const { callApi } = useApiContext();
    const permissions = usePermissions();
    const { pushParams } = useBreadcrumbsContext();
    const { selectedProject, isAllProjectSelected } = useProjectContext();
    const navigate = useNavigate();
    const context = useContext(ViewContext);
    const { _t } = useTranslate();

    const {
        data: user,
        error,
        isLoading,
        refetch,
    } = useQuery(userId && ['user', userId], () => callApi({ url: `/api/user/${userId}` }));

    const { data: projects, refetch: refetchProjects, isLoading: projectsLoading } = useQuery(
        'userProjects',
        () => callApi({ url: `/api/projects/with-users` }),
    );

    const url =
        `/api/tickets?user_id=${userId}&limit=10` +
        (isAllProjectSelected ? '' : `&project_id=${selectedProject?.id}`);
    const { data: userTickets } = useQuery(
        ['userTickets', userId, selectedProject],
        () =>
            callApi({
                url,
            }),
        {
            enabled: !!selectedProject?.id || isAllProjectSelected,
        },
    );

    const { mutate: addProjectUser } = useMutation(
        (data) =>
            callApi({
                url: `/api/project/${data.project_id}/user`,
                method: 'POST',
                data,
            }),
        {
            onSuccess: () => {
                refetchProjects();
            },
        },
    );

    const userType = user?.data?.is_contact ? 'contact' : 'user';
    const fullName = user?.data
        ? `${user.data.first_name || ''} ${user.data.last_name || ''}`.trim()
        : '';

    const formattedProjects = projects?.data?.map((project) => {
        const role = project.users.find((u) => u.user_id === userId)?.role;

        return {
            ...project,
            isInProject: project.users.some((u) => u.user_id === userId),
            rawRole: role || '',
            role: role ? _t(`project.roles.${role}`) : _t('undefined'),
        };
    });

    const memberProjects = (formattedProjects || []).filter((p) => p.isInProject);
    const tickets = userTickets?.data || [];

    useEffect(() => {
        if (user?.data?.first_name) {
            pushParams({
                userId: user.data.first_name,
            });
        }
    }, [user]);

    if (isLoading) {
        return (
            <div className={Style.shell}>
                <Skeleton className="h-24 w-full" />
                <div className={Style.grid}>
                    <Skeleton className="h-64 w-full" />
                    <Skeleton className="h-64 w-full" />
                </div>
            </div>
        );
    }

    if (error) return <p className="text-sm text-error">Error: {error.message}</p>;
    if (!user || !user.data.first_name) return null;

    function removeFromProject(project) {
        context.modal.show(
            {
                title: _t('project.user.remove.label'),
                form: {},
                buttonText: _t('confirm'),
                text: _t('project.user.remove.text'),
                url: `/api/project/${project.id}/user`,
                method: 'DELETE',
                destructive: true,
                injectedData: {
                    user_id: userId,
                    project_id: project.id,
                },
            },
            (res) => {
                if (res.status === 200) {
                    refetchProjects();
                }
            },
        );
    }

    function addToProject(project) {
        addProjectUser({
            user_id: userId,
            project_id: project.id,
        });
    }

    function deleteUser() {
        context.modal.show(
            {
                title: _t(`${userType}.delete.label`),
                form: {},
                buttonText: _t(`${userType}.delete.label`),
                text: _t(`${userType}.delete.confirm`, {
                    email: user.data.email,
                    name: fullName,
                }),
                url: `/api/user/${userId}/organization`,
                method: 'DELETE',
                destructive: true,
            },
            (res) => {
                context.notification.show(
                    (user?.data?.first_name || '') + ' ' + _t('message.wasDeleted'),
                    'success',
                    true,
                );

                if (res.status === 200) {
                    userType === 'user'
                        ? navigate(`/app/settings/users`)
                        : navigate(`/app/contacts`);
                }
            },
        );
    }

    function editProjectUser(data) {
        context.modal.show(
            {
                title: _t(`${userType}.update.label`),
                form: {
                    role: {
                        label: _t('project.role'),
                        type: 'select',
                        value: data.rawRole || 'contact',
                        options: [
                            { value: 'admin', label: _t('permissions.admin') },
                            { value: 'contact', label: _t('contact.label.one') },
                        ],
                    },
                },
                buttonText: _t('update'),
                url: `/api/project/${data.id}/user`,
                method: 'PUT',
                injectedData: {
                    user_id: userId,
                    project_id: data.id,
                },
            },
            () => {
                refetchProjects();
            },
        );
    }

    function openProjectsPannel() {
        context.pannel.show({
            title: _t('contact.projects.manage'),
            component: (
                <ContactProjectsPannel
                    userId={userId}
                    userType={userType}
                    userName={fullName}
                    projects={formattedProjects}
                    loading={projectsLoading}
                    onAdd={addToProject}
                    onRemove={removeFromProject}
                    onEditRole={editProjectUser}
                    canManage={user.data.permission !== 'owner'}
                />
            ),
            styles: { width: 560 },
        });
    }


    const body = (
            <div className={Style.shell}>
                <Card className={Style.identityCard}>
                    <div className={Style.identity}>
                        <UserAvatar name={fullName || user.data.email} size="lg" />
                        <div className="min-w-0 flex-1">
                            <h2 className={Style.name}>{fullName || user.data.email}</h2>
                            {user.data.position && (
                                <p className={Style.position}>{user.data.position}</p>
                            )}
                            <div className="mt-1.5 flex flex-wrap items-center gap-2">
                                <Badge
                                    text={_t(`permissions.${userType}`)}
                                    variant="neutral"
                                    className="text-xs"
                                />
                                {user.data.email && (
                                    <span className={Style.meta}>
                                        <Icon image="mail" size={12} color="currentColor" />
                                        {user.data.email}
                                    </span>
                                )}
                            </div>
                        </div>
                        {user.data.permission !== 'owner' && (
                            <Button
                                text={_t('delete')}
                                variant="danger"
                                size="sm"
                                icon="trash"
                                action={deleteUser}
                            />
                        )}
                    </div>
                </Card>

                <div className={Style.grid}>
                    <Card title={_t(`${userType}.update.label`)}>
                        <UserForm
                            userType={userType}
                            user={user}
                            refetch={refetch}
                            permissions={permissions}
                        />
                    </Card>

                    <Card title={_t('ticket.latest')}>
                        <div className={Style.ticketList}>
                            {tickets.map((ticket) => (
                                <TicketItem
                                    key={ticket.id || ticket.number}
                                    data={ticket}
                                    onItemClick={() => {
                                        navigate(`/app/tickets?id=${ticket.number}`);
                                    }}
                                />
                            ))}
                            {tickets.length === 0 && (
                                <EmptyState
                                    bare
                                    icon="inbox"
                                    title={_t('ticket.empty')}
                                    text={_t('contact.detail.noTickets')}
                                />
                            )}
                        </div>
                    </Card>
                </div>

                <Card>
                    <div className={Style.projectsHeader}>
                        <div>
                            <h3 className={Style.projectsTitle}>{_t('project.label.multiple')}</h3>
                            <p className={Style.projectsHint}>
                                {_t('contact.projects.manageDescription', { name: fullName })}
                            </p>
                        </div>
                        {user.data.permission !== 'owner' && (
                            <Button
                                variant="secondary"
                                size="sm"
                                icon="folder"
                                text={_t('contact.projects.manage')}
                                action={openProjectsPannel}
                            />
                        )}
                    </div>

                    {memberProjects.length === 0 ? (
                        <EmptyState
                            bare
                            icon="folder"
                            title={_t('contact.detail.noProjects')}
                            buttonText={
                                user.data.permission !== 'owner'
                                    ? _t('contact.projects.openManage')
                                    : undefined
                            }
                            action={
                                user.data.permission !== 'owner' ? openProjectsPannel : undefined
                            }
                        />
                    ) : (
                        <ul className={Style.projectList}>
                            {memberProjects.map((project) => (
                                <li key={project.id} className={Style.projectRow}>
                                    <div className="min-w-0">
                                        <p className={Style.projectName}>{project.name}</p>
                                        <p className={Style.projectRole}>{project.role}</p>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </Card>
            </div>
    );

    if (userType === 'user') {
        return <SettingsWrapper>{body}</SettingsWrapper>;
    }

    return body;
};

export default EditUser;
